import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Rating, Stack, Switch, Tooltip, Typography } from "@mui/material";
import { IconArrowLeft, IconStar, IconTrash } from "@tabler/icons";
import PageContainer from "src/components/container/PageContainer";
import PageHeader from "src/components/shared/PageHeader";
import ResourceList from "src/components/shared/ResourceList";
import PersonAvatar from "src/components/shared/PersonAvatar";
import ActionButton from "src/components/shared/ActionButton";
import { formatDate, plural, relativeTime } from "src/components/shared/format";
import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";
import { caseIdFor } from "src/pages/requests/parts";
import { useCountry } from "src/pages/catalogue/context";
import { placeholderWarnings } from "src/pages/website-content/checks";
import { deleteReviews, getReviews, setReviewShown } from "./actions";

const nameOf = (review) => [review.client_first_name, review.client_last_name].filter(Boolean).join(" ").trim() || "Customer";
const isShown = (review) => Number(review.is_active) === 1;

// The team's test reviews ("567j", "yuhk", or from a "Test" account) get the
// same flag as test entries on the content pages, so nobody switches one on.
// A one-word comment of up to six letters or digits is almost always typing,
// unless it is a word customers really leave.
const SHORT_WORDS = /^(good|great|nice|super|ok|okay|fine|fast|best|wow|thanks|thank|merci|perfect|excellent|amazing|bravo|top)$/i;
function looksLikeTest(review) {
  const comment = String(review.comment || "").trim();
  if (placeholderWarnings(comment, nameOf(review)).includes("Looks like a test entry.")) return true;
  return /^[a-z0-9]{1,6}$/i.test(comment) && !SHORT_WORDS.test(comment);
}

// What needs a decision first: test entries to delete, then real reviews not
// yet on the website (switch on or leave off), then the ones already shown.
const rank = (review) => (looksLikeTest(review) ? 0 : isShown(review) ? 2 : 1);
const byNeed = (a, b) => rank(a) - rank(b) || new Date(b.created_date) - new Date(a.created_date);

function ShownSwitch({ review, disabled, onToggle }) {
  const [saving, setSaving] = useState(false);
  const shown = isShown(review);
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Switch
        checked={shown}
        disabled={disabled || saving}
        onChange={async (e) => {
          setSaving(true);
          await onToggle(review, e.target.checked);
          setSaving(false);
        }}
        inputProps={{ "aria-label": `Show ${nameOf(review)}’s review on safrati.ai` }}
      />
      <Typography variant="body2" color={shown ? "text.primary" : "text.secondary"} sx={{ whiteSpace: "nowrap" }}>
        {saving ? "Saving…" : shown ? "Shown" : "Hidden"}
      </Typography>
    </Stack>
  );
}

ShownSwitch.propTypes = { review: PropTypes.object.isRequired, disabled: PropTypes.bool, onToggle: PropTypes.func.isRequired };

const RequestReviews = () => {
  const { country_id: countryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const privileges = getLocalStorage(dataLocalStorage.privileges) || {};
  const canEdit = Number(privileges["4"]?.access_edit) === 1;
  const canDelete = Number(privileges["4"]?.access_delete) === 1;
  const country = useCountry(countryId, location.state);
  const countryName = country?.name?.trim();
  const [state, setState] = useState({ loading: true, failed: false, rows: null });
  const [deleting, setDeleting] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, failed: false }));
    getReviews(countryId, dispatch)
      .then((response) => setState({ loading: false, failed: false, rows: Array.isArray(response?.data) ? response.data : [] }))
      .catch(() => setState((s) => ({ ...s, loading: false, failed: true })));
  }, [countryId, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (review, shown) => {
    try {
      await setReviewShown(review.id, shown, dispatch);
      setState((s) => ({ ...s, rows: s.rows.map((r) => (r.id === review.id ? { ...r, is_active: shown ? 1 : 0 } : r)) }));
    } catch {
      /* the toast says why; the switch stays as it was */
    }
  };

  const openCase = (review) =>
    caseIdFor(review.request_id, dispatch)
      .catch(() => review.request_id)
      .then((id) => navigate(`/cases/${id}`));

  const all = (state.rows || []).slice().sort(byNeed);
  const q = search.trim().toLowerCase();
  const rows = q ? all.filter((r) => `${nameOf(r)} ${r.comment || ""}`.toLowerCase().includes(q)) : all;
  const tests = all.filter(looksLikeTest).length;
  const undecided = all.filter((r) => !looksLikeTest(r) && !isShown(r)).length;
  const shownCount = all.filter(isShown).length;
  const average = all.length ? all.reduce((sum, r) => sum + (Number(r.stars) || 0), 0) / all.length : 0;

  const summary = all.length
    ? [
        tests ? (tests === 1 ? "1 looks like a test entry to delete" : `${tests} look like test entries to delete`) : "",
        undecided ? `${plural(undecided, "review")} not on the website yet` : "",
        `${shownCount} shown`,
        `average ${average.toFixed(1)} out of 5`,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <PageContainer title={`Reviews${countryName ? ` · ${countryName}` : ""}`} description="Customer reviews of a destination">
      <ActionButton
        variant="text"
        startIcon={<IconArrowLeft size={16} />}
        onClick={() => navigate(countryName ? `/countries/${countryId}/profile` : "/countries")}
        sx={{ mb: 1, ml: -1.5 }}
      >
        {countryName ? `Back to ${countryName}` : "Back to Countries"}
      </ActionButton>
      <PageHeader
        title={countryName ? `Reviews · ${countryName}` : "Reviews"}
        subtitle={
          summary
            ? `${summary}. Only reviews switched on appear on the destination’s page on safrati.ai.`
            : "What customers wrote after their application. Only reviews switched on appear on the destination’s page on safrati.ai."
        }
        sx={{ mb: 2 }}
      />

      <ResourceList
        label="Reviews"
        items={rows}
        getKey={(r) => r.id}
        primaryLabel="Review"
        primary={(r) => ({
          title: nameOf(r),
          subtitle: (
            <Stack component="span" spacing={0.5} sx={{ display: "flex" }}>
              <Box component="span" sx={{ color: "text.primary", whiteSpace: "pre-wrap" }}>
                {r.comment?.trim() ? `“${r.comment.trim()}”` : "No comment written."}
              </Box>
              {looksLikeTest(r) && (
                <Box component="span">
                  <Chip size="small" variant="outlined" color="warning" label="Looks like a test entry" />
                </Box>
              )}
            </Stack>
          ),
          avatar: <PersonAvatar name={nameOf(r)} src={r.client_image} size={40} />,
        })}
        columns={[
          {
            key: "stars",
            label: "Rating",
            inCard: true,
            render: (r) => (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Rating value={Number(r.stars) || 0} readOnly size="small" aria-hidden />
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                  {Number(r.stars) || 0} of 5
                </Typography>
              </Stack>
            ),
          },
          {
            key: "shown",
            label: "On safrati.ai",
            inCard: true,
            render: (r) => <ShownSwitch review={r} disabled={!canEdit} onToggle={toggle} />,
          },
          {
            key: "date",
            label: "Written",
            hideBelow: "lg",
            render: (r) => (
              <Tooltip title={formatDate(r.created_date)}>
                <span style={{ whiteSpace: "nowrap" }}>{relativeTime(r.created_date)}</span>
              </Tooltip>
            ),
          },
        ]}
        onOpen={(r) => r.request_id && navigate(`/request/${r.request_id}`)}
        openLabel={(r) => `Open the request ${nameOf(r)} reviewed`}
        rowActions={(r) => [
          r.request_id && { label: `Open request #${r.request_id}`, onClick: () => navigate(`/request/${r.request_id}`) },
          r.request_id && { label: "Open the case", onClick: () => openCase(r) },
          canDelete && { label: "Delete", destructive: true, icon: <IconTrash size={16} />, onClick: () => setDeleting([r]) },
        ]}
        bulkActions={canDelete ? [{ label: "Delete", destructive: true, onClick: (selected) => setDeleting(selected) }] : undefined}
        search={all.length > 5 ? { value: search, onChange: setSearch, placeholder: "Name or words in the review" } : undefined}
        loading={state.loading}
        error={state.failed}
        onRetry={load}
        empty={{ icon: <IconStar size={32} stroke={1.5} />, title: "No reviews yet.", hint: "Customers can review a destination from their account after applying." }}
      />

      {deleting && (
        <Dialog open onClose={() => setDeleting(null)} maxWidth="xs" fullWidth aria-labelledby="review-delete-title">
          <DialogTitle id="review-delete-title">{deleting.length === 1 ? "Delete this review?" : `Delete ${deleting.length} reviews?`}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {deleting.length === 1
                ? `${nameOf(deleting[0])}’s review (${Number(deleting[0].stars) || 0} out of 5) is removed for good, from the CMS and from safrati.ai.`
                : `${deleting.map(nameOf).join(", ")}: these reviews are removed for good, from the CMS and from safrati.ai.`}{" "}
              To only take a review off the website, switch it off instead.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <ActionButton variant="text" size="medium" onClick={() => setDeleting(null)}>
              Keep {deleting.length === 1 ? "it" : "them"}
            </ActionButton>
            <ActionButton
              size="medium"
              tone="destructive"
              busyLabel="Deleting…"
              startIcon={<IconTrash size={18} />}
              onClick={async () => {
                const ids = deleting.map((r) => r.id);
                try {
                  await deleteReviews(ids, dispatch);
                  setState((s) => ({ ...s, rows: s.rows.filter((r) => !ids.includes(r.id)) }));
                  setDeleting(null);
                } catch {
                  /* the toast says why */
                }
              }}
            >
              {deleting.length === 1 ? "Delete the review" : `Delete ${deleting.length} reviews`}
            </ActionButton>
          </DialogActions>
        </Dialog>
      )}
    </PageContainer>
  );
};

export default RequestReviews;
